import React, { useState } from 'react';

interface CollapsibleSectionProps {
  title: string;
  icon?: string;
  /** Small muted text after the title, e.g. a count. */
  meta?: string;
  /** Rendered in the header, right of the toggle. Clicks do not toggle the section. */
  actions?: React.ReactNode;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

/**
 * Collapsible panel matching the Sleep Sessions section, so the patient page
 * reads consistently as sections are added.
 */
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title, icon, meta, actions, defaultExpanded = false, children,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          className="flex-1 flex items-center justify-between text-left hover:bg-gray-50 -m-2 p-2 rounded-lg transition-colors"
        >
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            {icon && <span className="mr-2">{icon}</span>}
            {title}
            {meta && <span className="ml-2 text-sm font-normal text-gray-500">{meta}</span>}
          </h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {isExpanded ? 'Click to collapse' : 'Click to expand'}
            </span>
            <span className={`text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </div>
        </button>
        {/* Outside the toggle button: nested buttons are invalid HTML and the
            click would also collapse the section. */}
        {actions && <div className="ml-4 flex-shrink-0">{actions}</div>}
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-100">{children}</div>
      )}
    </div>
  );
};

export default CollapsibleSection;
